import { useEffect, useState } from "react";
import { Eye, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const FIELDS = [
  ["prefix", "Prefiks numeracji", "Numer = prefiks/kolejny nr w miesiącu/MM/RRRR, np. FPF/WWW/1/09/2026"],
  ["seller_name", "Sprzedawca — nazwa"], ["seller_nip", "Sprzedawca — NIP"],
  ["seller_address", "Sprzedawca — adres (linie)", null, "textarea"],
  ["bank", "Bank i numer rachunku"], ["issuer", "Osoba wystawiająca"],
  ["service_name", "Nazwa usługi", "Znaczniki: {pakiet}, {okres}"],
  ["payment_method", "Forma płatności"],
  ["payment_days", "Termin płatności (dni)", null, "number"], ["vat_rate", "Stawka VAT (%)", null, "number"],
];

export function ProformaSettings() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/proforma-settings").then((r) => { setS(r.data); setSaved(JSON.stringify(r.data)); }).catch(() => toast.error("Nie udało się pobrać ustawień proformy."));
  }, []);
  if (!s) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-600" /></div>;
  const dirty = JSON.stringify(s) !== saved;
  const set = (k, type) => (e) => setS({ ...s, [k]: type === "number" ? Number(e.target.value) : e.target.value });

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/admin/proforma-settings", s);
      setSaved(JSON.stringify(s));
      toast.success("Ustawienia proformy zapisane.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać.");
    } finally {
      setBusy("");
    }
  };
  const preview = async () => {
    setBusy("preview");
    try {
      const r = await api.post("/admin/proforma-settings/preview", s, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank", "noopener");
    } catch {
      toast.error("Nie udało się wygenerować podglądu.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="proforma-settings" className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-5">
      <div>
        <h2 className="font-display font-bold text-lg">Faktura pro forma</h2>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          Proforma jest wystawiana automatycznie przy każdym zamówieniu (numer nadawany raz, kolejno w miesiącu) i dołączana jako link w e-mailu obok umowy;
          klient pobiera ją także ze strony potwierdzenia i strony płatności. Kwota = cena pakietu netto za wybrany okres + VAT.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, hint, type]) => (
          <div key={k} className={type === "textarea" || k === "service_name" || k === "bank" ? "sm:col-span-2" : ""}>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{label}</label>
            {type === "textarea" ? (
              <Textarea data-testid={`proforma-${k}`} rows={2} value={s[k] ?? ""} onChange={set(k)} className="bg-background" />
            ) : (
              <Input data-testid={`proforma-${k}`} type={type === "number" ? "number" : "text"} value={s[k] ?? ""} onChange={set(k, type)} className="bg-background" />
            )}
            {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3">
        {dirty && <span className="text-xs text-amber-600 dark:text-amber-400">Niezapisane zmiany</span>}
        <button type="button" data-testid="proforma-preview-button" onClick={preview} disabled={!!busy} className={`${btn} border border-border hover:bg-muted`}>
          {busy === "preview" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Podgląd PDF
        </button>
        <button type="button" data-testid="proforma-save-button" onClick={save} disabled={!!busy || !dirty} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
          {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz
        </button>
      </div>
    </div>
  );
}
